import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core'
import { Auth, TotpSecret } from '@angular/fire/auth'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import QRCode from 'qrcode'
import { lastValueFrom } from 'rxjs'

import { ButtonComponent } from '../../components/button/button.component'
import { AuthService, MFA_SETTINGS_PATH } from '../../services/auth.service'
import { getTitle } from '../../util/accessibility'
import { captureException } from '../../util/captureException'
import { MfaService } from '../../services/mfa.service'
import { environment } from '../../../environments/environment'

@Component({
  selector: 'app-mfa-totp-setup-page',
  standalone: true,
  imports: [MatIcon, ButtonComponent, ReactiveFormsModule],
  templateUrl: './mfa-totp-setup-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mfa-totp-setup-page.component.scss',
})
export class MfaTotpSetupPageComponent implements OnInit {
  busy = false
  preparing = true
  needsNewSecret = false
  errorMessage = ''
  qrCodeDataUrl = ''
  private secret: TotpSecret | null = null
  form = new FormGroup({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{6}$/)],
    }),
  })
  constructor(
    private auth: Auth,
    private authService: AuthService,
    private mfa: MfaService,
    private router: Router,
    title: Title,
  ) {
    title.setTitle(getTitle('認証アプリの設定'))
  }
  async ngOnInit(): Promise<void> {
    await this.prepareSecret()
  }
  private async prepareSecret(): Promise<void> {
    const user = this.auth.currentUser
    if (!user || !user.email) {
      await this.router.navigate(['/login'])
      return
    }
    try {
      const secret = await lastValueFrom(this.mfa.generateTotpSecret(user))
      this.qrCodeDataUrl = await QRCode.toDataURL(
        secret.generateQrCodeUrl(user.email, environment.totpIssuer),
        { errorCorrectionLevel: 'M', margin: 1, width: 200 },
      )
      this.needsNewSecret = false
      this.secret = secret
    } catch (error) {
      await this.handleError(error)
    } finally {
      this.preparing = false
    }
  }
  async regenerateSecret(): Promise<void> {
    if (this.preparing || this.busy) return
    this.preparing = true
    this.errorMessage = ''
    this.form.controls.code.reset()
    await this.prepareSecret()
  }
  async submit(): Promise<void> {
    if (this.form.invalid || this.busy || !this.secret) return
    const user = this.auth.currentUser
    if (!user) {
      await this.router.navigate(['/login'])
      return
    }
    this.busy = true
    this.errorMessage = ''
    try {
      await lastValueFrom(
        this.mfa.enrollTotp(
          user,
          this.secret,
          this.form.controls.code.value,
          '認証アプリ',
        ),
      )
      this.authService.clearMfaEnrollmentOrigin()
      const returnUrl = this.authService.consumeReturnUrl() || '/home'
      this.authService.clearLoginState()
      await this.router.navigateByUrl(returnUrl)
    } catch (error) {
      await this.handleError(error)
    } finally {
      this.busy = false
    }
  }
  async back(): Promise<void> {
    const fromSettings = this.authService.isFromMfaSettings()
    this.authService.clearMfaEnrollmentOrigin()
    if (fromSettings && this.auth.currentUser) {
      this.authService.consumeReturnUrl()
      await this.router.navigate([MFA_SETTINGS_PATH])
    } else {
      await this.router.navigate(['/auth/mfa-select'])
    }
  }
  private async handleError(error: unknown): Promise<void> {
    const code = (error as { code?: string }).code
    if (code === 'auth/requires-recent-login') {
      if (this.authService.isFromMfaSettings()) {
        await this.authService.startReauthentication({ type: 'add-totp' })
      } else {
        await this.authService.signOutPreservingReturnUrl()
        this.authService.redirectToLogin({ reason: 'reauth' })
      }
    } else if (code === 'auth/operation-not-allowed') {
      this.errorMessage =
        'この環境では認証アプリを利用できません。管理者にお問い合わせください'
      captureException(error)
    } else if (code === 'auth/unverified-email') {
      this.errorMessage =
        'メールアドレスの確認が必要です。メールを確認して、リンクをクリックしてください。'
      try {
        await this.authService.sendEmailVerification()
      } catch {
        this.errorMessage = 'メールの送信に失敗しました。再試行してください。'
      }
    } else if (
      code === 'auth/code-expired' ||
      code === 'auth/totp-challenge-timeout'
    ) {
      this.secret = null
      this.qrCodeDataUrl = ''
      this.needsNewSecret = true
      this.errorMessage =
        '登録の有効期限が切れました。QR コードを再生成し、認証アプリに新しいシークレットを登録してください。'
    } else if (code === 'auth/maximum-second-factor-count-exceeded') {
      this.errorMessage =
        '登録できる手段の上限です。設定画面で登録済みの手段を確認してください。'
    } else if (code === 'auth/invalid-verification-code') {
      this.errorMessage =
        '認証コードが無効か、有効期限が切れています。新しいコードを入力し、端末の時刻が自動設定になっているか確認してください。'
    } else {
      this.errorMessage = '認証アプリの設定に失敗しました。再度お試しください。'
    }
  }
}
